import React, { useMemo } from 'react'
import { ArrowDown, ArrowUp, Ban, History, RotateCcw } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import SectionHeading from '@/components/dashboard/SectionHeading'
import CompetitorBadge from '@/components/market/CompetitorBadge'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import type {
  MarketCompetitor,
  MarketMoveDirection,
  MarketMoveItem,
  MarketMoves as MovesData,
  MarketNight,
} from '@/types/market'

/**
 * What changed since the previous check day, competitor by competitor. Measurements only: who
 * raised or lowered which nights, never why. Before a second day of checks exists there is nothing
 * to compare, and the section says exactly that.
 */

const DIRECTION: Record<MarketMoveDirection, { icon: React.ElementType; className: string; label: string }> = {
  up: { icon: ArrowUp, className: 'text-emerald-600', label: 'up' },
  down: { icon: ArrowDown, className: 'text-rose-600', label: 'down' },
  sold_out: { icon: Ban, className: 'text-slate-500', label: 'sold out' },
  back_on_sale: { icon: RotateCcw, className: 'text-slate-500', label: 'back on sale' },
}

/** Items arrive in competitor order, then date: grouping is a walk, not a sort. */
export function groupMoves(
  items: MarketMoveItem[]
): { marketPropertyId: number; name: string; items: MarketMoveItem[] }[] {
  const groups: { marketPropertyId: number; name: string; items: MarketMoveItem[] }[] = []
  for (const item of items) {
    const last = groups[groups.length - 1]
    if (last && last.marketPropertyId === item.marketPropertyId) last.items.push(item)
    else groups.push({ marketPropertyId: item.marketPropertyId, name: item.name, items: [item] })
  }
  return groups
}

/** One competitor's nights as a strip: which nights moved, and which way. Decorative. */
function MoveStrip({ nights, marketPropertyId }: { nights: MarketNight[]; marketPropertyId: number }) {
  const first = nights[0]
  const last = nights[nights.length - 1]
  return (
    <div aria-hidden="true" className="w-full max-w-[300px]">
      <div className="grid h-2.5 gap-px" style={{ gridTemplateColumns: `repeat(${nights.length}, minmax(0, 1fr))` }}>
        {nights.map((night) => {
          const cell = night.cells.find((c) => c.marketPropertyId === marketPropertyId)
          const cls =
            cell?.availabilityChange === 'sold_out'
              ? 'bg-slate-700'
              : cell?.availabilityChange === 'back_on_sale'
                ? 'bg-slate-400'
                : cell?.direction === 'up'
                  ? 'bg-emerald-500'
                  : cell?.direction === 'down'
                    ? 'bg-rose-500'
                    : 'bg-slate-100'
          return (
            <span
              key={night.date}
              title={cell?.changeLabel ? `${night.label}: ${cell.changeLabel}` : night.label}
              className={cn('rounded-[1px]', cls)}
            />
          )
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{first.isToday ? 'Tonight' : first.label}</span>
        <span>{last.label}</span>
      </div>
    </div>
  )
}

export default function MarketMoves({
  moves,
  competitors,
  numbers,
  nights,
  currency,
  onShow,
}: {
  moves: MovesData
  currency: string
  competitors: MarketCompetitor[]
  numbers: Map<number, number>
  nights: MarketNight[]
  onShow: (date: string) => void
}) {
  const groups = useMemo(() => groupMoves(moves.items), [moves.items])
  const hasItems = moves.status === 'ready' && moves.items.length > 0
  const counts: { key: MarketMoveDirection; n: number }[] = [
    { key: 'up', n: moves.upCount },
    { key: 'down', n: moves.downCount },
    { key: 'sold_out', n: moves.soldOutCount },
    { key: 'back_on_sale', n: moves.backOnSaleCount },
  ]
  const known = new Set(competitors.map((c) => c.marketPropertyId))

  return (
    <section aria-labelledby="market-moves" className="scroll-mt-6" id="market-moves-section">
      <SectionHeading
        id="market-moves"
        title="Market moves"
        subtitle={hasItems ? moves.sentence : undefined}
        help={{
          content: `Competitor list prices that changed by at least 3% and ${currencySymbol(currency)}3 for the same night between the last two days of checks. Smaller changes are not listed.`,
        }}
      >
        {hasItems && moves.totalCount > moves.items.length && (
          <span className="text-xs text-muted-foreground">
            Showing {moves.items.length} of {moves.totalCount}
          </span>
        )}
      </SectionHeading>

      <DashboardCard padded={false}>
        {!hasItems ? (
          <div className="flex items-start gap-3 px-5 py-5" data-testid="moves-quiet">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
              <History className="h-4 w-4" />
            </span>
            <p className="pt-1.5 text-sm text-muted-foreground">{moves.sentence}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-border/40 px-5 py-3">
              {moves.comparedFromLabel && moves.comparedToLabel && (
                <span className="mr-2 text-xs text-muted-foreground">
                  {moves.comparedFromLabel} to {moves.comparedToLabel}
                </span>
              )}
              {counts
                .filter((c) => c.n > 0)
                .map(({ key, n }) => {
                  const d = DIRECTION[key]
                  const Icon = d.icon
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60"
                    >
                      <Icon className={cn('h-3 w-3', d.className)} />
                      {n} {d.label}
                    </span>
                  )
                })}
            </div>
            <ul className="divide-y divide-border/40">
              {groups.map((group) => (
                <li key={group.marketPropertyId} className="px-5 py-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground">
                      <CompetitorBadge number={numbers.get(group.marketPropertyId)} />
                      <span className="truncate">{group.name}</span>
                    </p>
                    {known.has(group.marketPropertyId) && nights.length > 0 && (
                      <MoveStrip nights={nights} marketPropertyId={group.marketPropertyId} />
                    )}
                  </div>
                  <ul className="mt-2.5 space-y-2">
                    {group.items.map((item) => {
                      const d = DIRECTION[item.direction]
                      const Icon = d.icon
                      return (
                        <li key={`${item.direction}-${item.startDate}`} className="flex items-start gap-2.5">
                          <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', d.className)} />
                          <p className="min-w-0 flex-1 text-[13px] leading-snug text-foreground">{item.sentence}</p>
                          <button
                            type="button"
                            onClick={() => onShow(item.startDate)}
                            title="Show on chart"
                            className="shrink-0 cursor-pointer rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-inset ring-border transition-colors hover:bg-primary/5 hover:text-primary hover:ring-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          >
                            {item.rangeLabel}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </DashboardCard>
    </section>
  )
}
